import React from 'react'
import { EmptyState } from './EmptyState'
import { Spinner } from './Spinner'
import { cn } from './cn'

function SkeletonRows({ columns, rows = 5 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex}>
          {columns.map((col) => (
            <td key={col.key || col.header} className="px-4 py-3">
              <div className="h-4 animate-pulse rounded bg-secondary-200 dark:bg-secondary-700" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

export function DataTable({
  columns,
  data,
  loading = false,
  emptyTitle = 'No data',
  emptyDescription,
  emptyAction,
  getRowKey = (row, index) => row.id ?? index,
  minWidth = 'min-w-[720px]',
  className,
}) {
  const colCount = columns.length

  return (
    <div className={cn('card overflow-hidden p-0', className)}>
      <div className="overflow-x-auto">
        <table className={cn('w-full text-left text-sm', minWidth)}>
          <thead className="sticky top-0 z-[1] border-b border-muted bg-secondary-50 dark:border-muted-dark dark:bg-secondary-800/80">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key || col.header}
                  className={cn(
                    'px-4 py-3 text-xs font-semibold uppercase tracking-wider text-text-muted dark:text-text-muted-dark',
                    col.className
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-muted dark:divide-muted-dark">
            {loading && <SkeletonRows columns={columns} />}
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={colCount}>
                  <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
                </td>
              </tr>
            )}
            {!loading &&
              data.map((row, index) => (
                <tr
                  key={getRowKey(row, index)}
                  className="transition-colors hover:bg-secondary-50/80 dark:hover:bg-secondary-800/40"
                >
                  {columns.map((col) => (
                    <td key={col.key || col.header} className={cn('px-4 py-3 text-secondary-700 dark:text-secondary-200', col.cellClassName)}>
                      {col.render ? col.render(row, index) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function TableLoadingOverlay({ label = 'Loading…' }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16">
      <Spinner />
      <span className="text-sm text-text-muted dark:text-text-muted-dark">{label}</span>
    </div>
  )
}
